/*â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
 ðŸŒ Project: Ring2All Platform
 ðŸ§‘â€ðŸ’» Author: Rodrigo Cuadra
 ðŸ¢ Company: Ring2All LLC
 ðŸ“„ File: UserFormView.tsx
 ðŸ—“ï¸ Created: 2025-01-05
 ðŸ§© Description:
    User creation and editing form component with comprehensive validation,
    avatar upload, tenant management, and role assignment functionality.

 ðŸ”§ Notes:
    - Refactored to use modular hooks and components
    - Integrates with authentication store for user data management
    - Supports avatar upload with image preview and validation
    - Includes tenant selection with multi-select capabilities
    - Uses custom Toggle component for boolean fields
    - Implements comprehensive form validation with error handling

 ðŸ” Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, ArrowLeft, Search, AlertCircle, Copy, Upload, User, Building, X, Shield, Plus, Grid, Info } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useDebugMode } from '../../hooks/useDebugMode';
import { FormBox } from '../../components/ui/FormBox';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { MultiSelectModal } from '../../components/ui/MultiSelectModal';
import { MultiSelectButton } from '../../components/ui/MultiSelectButton';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { navigateToUserDuplicate } from './usersActions';
import { deleteUser } from './api/usersApi';
import {
  useUserForm,
  useUserData,
  useAvatarUpload,
  useQuickList,
  useUserSubmit,
} from './hooks';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { User as UserType } from './types/users.types';
import { UserBasicInfoSection } from './components/UserBasicInfoSection';
import { UserProfilesSection } from './components/UserProfilesSection';
import { UserAppsSection } from './components/UserAppsSection';

export default function UserFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user: currentUser } = useAuthStore();
  const { showSuccess, showError, showInfo, notifications, removeNotification } = useNotifications();
  const { isActive: debugModeActive } = useDebugMode();
  const tenantSlug = currentUser?.tenant?.slug || t('users.defaultTenant');

  const mode = id === 'new' ? 'create' : 'edit';

  // Form state management
  const {
    formData,
    originalData,
    setFormData,
    setOriginalData,
    handleInputChange,
    handleMultiSelectChange,
    resetForm,
    hasChanges: formHasChanges,
  } = useUserForm(mode);

  // Avatar state
  const [avatar, setAvatar] = useState<string | null>(null);
  const [originalAvatar, setOriginalAvatar] = useState<string | null>(null);

  // Avatar upload hook
  const {
    selectedFile,
    uploading,
    handleAvatarSelect,
    handleAvatarRemove,
    uploadAvatarAfterCreate,
  } = useAvatarUpload(mode, id, avatar, setAvatar);

  // Load user data and options
  const { profiles, logProfiles, tenants, finalMenuItems, isLoadingMenuItems } = useUserData(
    id,
    duplicateId,
    mode,
    setFormData,
    setOriginalData,
    setAvatar,
    setOriginalAvatar,
  );

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('general');

  // Form submission
  const { saving, errors, setErrors, hasChanges, handleSubmit } = useUserSubmit(
    mode,
    id,
    formData,
    originalData,
    setOriginalData,
    uploadAvatarAfterCreate,
    setActiveTab
  );

  // Quick list modal
  const [showQuickList, setShowQuickList] = useState(false);
  const { quickListSearch, setQuickListSearch, filteredQuickList, isQuickListLoading } =
    useQuickList(showQuickList);

  // Multi-select modal states
  const [showTenantModal, setShowTenantModal] = useState(false);

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear page title

  // Navigate to list
  const handleGoToList = () => {
    navigate('/users');
  };

  // Handle cancel
  const handleCancel = () => {
    resetForm();
    setAvatar(originalAvatar);
    setErrors({});
  };

  // Handle delete
  const handleDeleteClick = () => {
    if (mode !== 'edit' || !id) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (mode !== 'edit' || !id) return;

    setIsDeleting(true);
    try {
      await deleteUser(id, tenantSlug, currentUser?.id);
      showSuccess(t('users.userDeleted'));
      navigate('/users');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      showError(msg || t('users.errorDeleting'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (mode === 'edit' && id) {
      navigateToUserDuplicate(navigate, id);
    }
  }, [mode, id, navigate]);

  // Check if form has changes (including avatar)
  const hasChangesWithAvatar = hasChanges || selectedFile !== null;

  // Handle save internal (wraps handleSubmit to return User)
  const handleSaveInternal = useCallback(async (): Promise<UserType | undefined> => {
      // hasChanges validation removed

    const savedData = await handleSubmit();

    // Update original data after successful save
    if (savedData) {
      setOriginalData({
        ...formData,
        password: '', // Password is never in originalData
      });
    }

    return savedData;
  }, [handleSubmit, hasChangesWithAvatar, showInfo, t, mode, id, formData, setOriginalData]);

  // Save with action hook
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/users',
    newPath: '/users/new',
    getEditPath: (savedData) => `/users/${savedData.id}`,
    mode,
    storageKey: 'users-save-action',
  });


  return (
    <div className="p-6"><ModuleFormHeader
        icon={<User className="h-4 w-4 text-primary" />}
        title={mode === 'edit' ? `${t('users.editUser')} - ${formData.username || formData.fullName || ''}` : t('users.createUser')}
        backButtonText={t('common:list')}
        onBack={handleGoToList}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('users.quickList'),
            onClick: () => setShowQuickList(true),
          },
          ...(mode === 'edit' && id
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: handleDuplicateFromForm,
              },
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('users.addUser'),
            onClick: () => navigate('/users/new')
          }
        ]}
      />

      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start mb-4 bg-card border border-border rounded-lg p-1 overflow-x-auto flex-nowrap">
              <TabsTrigger value="general" icon={<Info className="h-4 w-4" />}>
                {t('users.tabs.generalData', 'General Data')}
              </TabsTrigger>
              {tenants.length > 1 && (
                <TabsTrigger value="tenants" icon={<Building className="h-4 w-4" />}>
                  {t('users.tabs.tenants', 'Tenants')}
                </TabsTrigger>
              )}
              <TabsTrigger value="apps" icon={<Grid className="h-4 w-4" />}>
                {t('users.tabs.apps', 'Available Apps')}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4">
              <UserBasicInfoSection
                formData={formData}
                errors={errors}
                mode={mode}
                debugModeActive={debugModeActive}
                finalMenuItems={finalMenuItems}
                isLoadingMenuItems={isLoadingMenuItems}
                onInputChange={handleInputChange}
                t={t}
              />

              <UserProfilesSection
                formData={formData}
                avatar={avatar}
                uploading={uploading}
                debugModeActive={debugModeActive}
                onInputChange={handleInputChange}
                onAvatarSelect={handleAvatarSelect}
                onAvatarRemove={handleAvatarRemove}
                t={t}
              />
            </TabsContent>

            {tenants.length > 1 && (
              <TabsContent value="tenants" className="space-y-4">
                <FormBox
                  title={t('users.sections.tenantAccess')}
                  icon={<Building className="h-5 w-5" />}
                >
                  <MultiSelectButton
                    items={tenants}
                    selectedIds={formData.tenantIds}
                    onClick={() => setShowTenantModal(true)}
                    label={t('users.availableTenants')}
                    placeholder={t('users.selectTenants')}
                    icon={Building}
                    maxPreviewItems={2}
                  />
                </FormBox>
              </TabsContent>
            )}

            <TabsContent value="apps" className="space-y-4">
              <UserAppsSection
                formData={formData}
                profiles={profiles}
                logProfiles={logProfiles}
                onInputChange={handleInputChange}
                t={t}
              />
            </TabsContent>
          </Tabs>
        </form>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={mode === 'edit' ? handleDeleteClick : undefined}
        deleteDisabled={formData.isPrimaryAdmin}
        deleteTooltip={formData.isPrimaryAdmin ? t('users.tooltips.cannotDeletePrimary') : undefined}
        isEditMode={mode === 'edit'}
        isSaving={saving}
        saveText={t('common:save')}
        cancelText={t('common:cancel')}
        deleteText={t('common:delete')}
        hasChanges={hasChangesWithAvatar}
        useSaveDropdown={true}
        showSave={true}
        saveDropdownStorageKey="users-save-action"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('users.deleteUserTitle')}
        message={
          formData.username || formData.fullName || formData.email
            ? `${t('users.areYouSureDeleteUser')} "${formData.username || formData.fullName || formData.email}"?`
            : t('users.confirmDelete')
        }
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('users.quickList', 'Users')}
        items={filteredQuickList.filter((u): u is typeof u & { id: string } => !!u.id)}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(user) => {
          setShowQuickList(false);
          navigate(`/users/${user.id}`);
        }}
        currentItemId={mode === 'edit' ? id : null}
        renderItem={(user, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{user.fullName || user.username}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              {user.department && (
                <p className="text-sm text-muted-foreground">{user.department}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('users.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t(
          'users.quickListSearch',
          'Search by username, email, name or department',
        )}
        emptyMessage={t('users.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('users.quickListNoResults', 'No users found')}
        headerIcon={<User className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Tenant Selection Modal */}
      <MultiSelectModal
        isOpen={showTenantModal}
        onClose={() => setShowTenantModal(false)}
        title={t('users.availableTenants')}
        description={t('users.selectTenantsDescription')}
        items={tenants}
        selectedIds={formData.tenantIds}
        onSelectionChange={(selectedIds) => {
          handleMultiSelectChange('tenantIds', selectedIds);
        }}
        searchPlaceholder={t('users.searchTenants')}
        icon={Building}
      />
    </div>
  );
}
